import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { Video, Upload, Trash2, Loader2 } from 'lucide-react';
import { removeListingVideo } from '../lib/api';
import { uploadVideoToListing, validateVideoFile, probeVideoDuration } from '../lib/videoUpload';
import { useLanguage } from '../context/LanguageContext';

const VideoUploader = ({
  listingId,
  currentVideoUrl,
  currentVideoThumbnail,
  currentVideoDuration,
  onChange,
  pendingFile,
  onPendingFileChange,
}) => {
  const { language } = useLanguage();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [removing, setRemoving] = useState(false);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState(null);

  const t = (fr, en) => (language === 'fr' ? fr : en);

  // Generate preview URL for pending file
  useEffect(() => {
    if (pendingFile) {
      const url = URL.createObjectURL(pendingFile);
      setPendingPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPendingPreviewUrl(null);
  }, [pendingFile]);

  const handleSelect = () => fileRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;

    // PENDING MODE — no listing_id yet (creation flow)
    if (!listingId) {
      const err = validateVideoFile(file, undefined, language);
      if (err) { toast.error(err); return; }
      const duration = await probeVideoDuration(file);
      if (duration && duration > 60) {
        toast.error(t(`Vidéo trop longue (${Math.round(duration)}s). Maximum 60s.`, `Video too long (${Math.round(duration)}s). Max 60s.`));
        return;
      }
      onPendingFileChange?.(file);
      return;
    }

    // IMMEDIATE UPLOAD MODE (edit flow)
    setUploading(true);
    setProgress(0);
    try {
      const updated = await uploadVideoToListing(file, listingId, setProgress, language);
      toast.success(t('Vidéo ajoutée avec succès', 'Video uploaded successfully'));
      onChange?.(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || t('Erreur', 'Error'));
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 1500);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!listingId) {
      // pending mode: just clear the file
      onPendingFileChange?.(null);
      return;
    }
    if (!window.confirm(t('Supprimer la vidéo de cette annonce ?', 'Remove the video from this listing?'))) return;
    setRemoving(true);
    try {
      const res = await removeListingVideo(listingId);
      toast.success(t('Vidéo supprimée', 'Video removed'));
      onChange?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Erreur', 'Error'));
    } finally {
      setRemoving(false);
    }
  };

  const hasMedia = !!currentVideoUrl || !!pendingPreviewUrl;
  const displayUrl = currentVideoUrl || pendingPreviewUrl;
  const displayThumb = currentVideoThumbnail;
  const displayDuration = currentVideoDuration;

  return (
    <Card className="p-4 border-dashed" data-testid="video-uploader">
      <input
        ref={fileRef}
        type="file"
        accept="video/mp4,video/quicktime,video/webm,video/x-m4v,.mp4,.mov,.webm,.m4v"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {hasMedia ? (
        <div className="space-y-3">
          <div className="relative rounded-lg overflow-hidden bg-black aspect-video max-h-64">
            <video
              src={displayUrl}
              poster={displayThumb}
              controls
              preload="metadata"
              playsInline
              className="w-full h-full object-contain"
              data-testid="video-preview"
            />
            {!listingId && pendingPreviewUrl && (
              <span className="absolute top-2 left-2 text-xs font-semibold uppercase text-white bg-amber-500/90 px-2 py-0.5 rounded-full">
                {t('À envoyer après publication', 'Will upload after publish')}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground flex items-center gap-2">
              <Video className="h-4 w-4" />
              {displayDuration ? `${Math.round(displayDuration)}s` : (pendingFile ? `${(pendingFile.size / (1024 * 1024)).toFixed(1)} MB` : '')}
            </span>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={handleSelect} disabled={uploading}>
                <Upload className="h-4 w-4 mr-1" />
                {t('Remplacer', 'Replace')}
              </Button>
              <Button type="button" variant="outline" size="sm" className="text-red-600" onClick={handleRemove} disabled={removing} data-testid="video-remove-btn">
                {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-6 space-y-3">
          <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
            <Video className="h-7 w-7 text-primary" />
          </div>
          <div>
            <p className="font-medium">{t('Ajouter une vidéo (recommandée)', 'Add a video (recommended)')}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('Boostez votre annonce — MP4 ou MOV, 3 à 60s, max 100 MB', 'Boost your listing — MP4 or MOV, 3-60s, max 100 MB')}
            </p>
          </div>
          {uploading ? (
            <div className="space-y-2">
              <Progress value={progress} className="h-2" data-testid="video-progress" />
              <p className="text-xs text-muted-foreground">{progress}%</p>
            </div>
          ) : (
            <Button type="button" onClick={handleSelect} className="gap-2" data-testid="video-select-btn">
              <Upload className="h-4 w-4" />
              {t('Sélectionner une vidéo', 'Select a video')}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};

export default VideoUploader;
